import { useQuery } from '@apollo/client';
import { Footer } from '@ifca-root/react-component/src/components/Footer/Footer';
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper';
import Loading from '@ifca-root/react-component/src/components/Loading/Loading';
import { Overlay } from '@ifca-root/react-component/src/components/Overlay/Overlay';
import ShareIcon from '@material-ui/icons/Share';
import { getAccessToken } from 'AccessToken';
import ShareDialog from 'components/Dialog/ShareDialog';
import { MainHeaderCustomer } from 'components/Layout/MainHeaderCustomer';
import {
	OrderDiscountStatus,
	PaymentClass,
	useCreateReceiptAttachmentMutation,
	useGetBillQuery,
	useGetMenuXDebtorListLazyQuery,
	useGetMenuXInhouseListLazyQuery,
	useGetOfficerLazyQuery,
	useGetStaffLazyQuery,
} from 'generated/graphql';
import { sortDescByKey } from 'helpers/hooks/stringNumberFunction/sortByKey';
import * as htmlToImage from 'html-to-image';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { GET_PATRON_RECEIPT_BILL } from './GraphQL/PatronReceiptBill';
import {
	billReceiptDisplay,
	printReceipt,
} from './ReceiptFunctions/ReceiptFunctions';

export const Receipt = () => {
	const { menuID, orderID, tableID, outletID, billID } = useParams<
		Record<string, any>
	>();

	const hasAuth = !!getAccessToken();

	const [hotelGuest, setHotelGuest] = useState({} as any);
	const [hotelDebtor, setHotelDebtor] = useState({} as any);
	const [officer, setOfficer] = useState({} as any);
	const [staff, setStaff] = useState({} as any);
	const [latestPayment, setLatestPayment] = useState([] as any);
	const [primaryPayment, setPrimaryPayment] = useState({} as any);
	const [outlet, setOutlet] = useState({} as any);
	const [negOrderItem, setNegOrderItem] = useState({});
	const [receiptDataURL, setReceiptDataURL] = useState(null);
	const [outletLogo, setOutletLogo] = useState<string | null>(null);

	const handleSort = arr => {
		let temp = arr?.slice();
		return sortDescByKey(temp, 'createdTs');
	};

	const [
		loadMenuXInhouse,
	] = useGetMenuXInhouseListLazyQuery({
		fetchPolicy: 'network-only',
		onCompleted: ({ getMenuXInhouseList }) => {
			setHotelGuest(
				getMenuXInhouseList?.data?.MenuXInhouseList?.filter(
					guest => guest?.RegistrationID === primaryPayment?.registrationID,
				)[0],
			);
		},
	});

	const [
		loadMenuXDebtor,
	] = useGetMenuXDebtorListLazyQuery({
		fetchPolicy: 'network-only',
		onCompleted: ({ getMenuXDebtorList }) => {
			setHotelDebtor(
				getMenuXDebtorList?.data?.MenuXDebtorList?.filter(
					guest => guest?.RegistrationID === primaryPayment?.registrationID,
				)[0],
			);
		},
	});

	const [
		loadOfficer,
	] = useGetOfficerLazyQuery({
		fetchPolicy: 'network-only',
		onCompleted: ({ getOfficer }) => {
			setOfficer(getOfficer);
		},
	});

	const [
		loadStaff,
	] = useGetStaffLazyQuery({
		fetchPolicy: 'network-only',
		onCompleted: ({ getStaff }) => {
			setStaff(getStaff);
		},
	});

	const applyBillData = (bills: any[]) => {
		const bill = bills?.[0];
		if (!bill) {
			return;
		}

		const sortedPayments = handleSort(bill?.payments) || [];
		const payment = sortedPayments[0];
		setLatestPayment(sortedPayments);
		setPrimaryPayment(payment || {});
		setOutlet(bill?.outlet);

		const temp = {};
		bill?.orderItem?.map(el => {
			if (el?.refID && el?.quantity < 0) {
				if (el?.refID in temp) {
					temp[el?.refID] += el?.quantity * -1;
				} else {
					temp[el?.refID] = el?.quantity * -1;
				}
			}
		});
		setNegOrderItem({ ...temp });

		const logoUrl = bill?.outlet?.logo?.fileURL;
		if (logoUrl) {
			fetch(logoUrl, { mode: 'cors', cache: 'no-cache' })
				.then(res => {
					if (!res.ok) throw new Error(`HTTP ${res.status}`);
					return res.blob();
				})
				.then(blob => {
					const mime = blob.type?.startsWith('image/')
						? blob.type
						: 'image/jpeg';
					const imageBlob =
						blob.type === mime ? blob : new Blob([blob], { type: mime });
					const reader = new FileReader();
					reader.onloadend = () => {
						setOutletLogo((reader.result as string) || null);
					};
					reader.onerror = () => setOutletLogo(null);
					reader.readAsDataURL(imageBlob);
				})
				.catch(() => setOutletLogo(null));
		} else {
			setOutletLogo(null);
		}

		if (!hasAuth) {
			return;
		}

		if (
			bill?.outlet?.outletChannel?.hotelID &&
			payment?.registrationID &&
			payment?.paymentClass === PaymentClass.Debtor
		) {
			loadMenuXDebtor({
				variables: {
					hotelID: bill?.outlet?.outletChannel?.hotelID,
					outletID: outletID,
				},
			});
		} else if (
			bill?.outlet?.outletChannel?.hotelID &&
			payment?.registrationID &&
			payment?.paymentClass === PaymentClass.Room
		) {
			loadMenuXInhouse({
				variables: {
					hotelID: bill?.outlet?.outletChannel?.hotelID,
					outletID: outletID,
				},
			});
		} else if (payment?.paymentClass === PaymentClass.Officer) {
			loadOfficer({
				variables: { ID: payment?.patronAccountID },
			});
		} else if (payment?.paymentClass === PaymentClass.Staff) {
			loadStaff({
				variables: { ID: payment?.patronAccountID },
			});
		}
	};

	const {
		loading: authBillLoading,
		called: authBillCalled,
		data: { getBill: authBill } = { getBill: [] },
	} = useGetBillQuery({
		skip: !hasAuth || !billID,
		fetchPolicy: 'network-only',
		variables: {
			ID: billID,
		},
		onCompleted: ({ getBill }) => {
			applyBillData(getBill);
		},
	});

	const {
		loading: publicBillLoading,
		called: publicBillCalled,
		data: publicBillData,
	} = useQuery(GET_PATRON_RECEIPT_BILL, {
		skip: hasAuth || !billID,
		fetchPolicy: 'network-only',
		variables: {
			billID,
			outletID,
		},
		onCompleted: data => {
			applyBillData(data?.getPatronReceiptBill || []);
		},
	});

	const getBill = hasAuth
		? authBill || []
		: publicBillData?.getPatronReceiptBill || [];
	const billLoading = hasAuth ? authBillLoading : publicBillLoading;
	const billCalled = hasAuth ? authBillCalled : publicBillCalled;

	const orderItems = getBill[0]?.orderItem
		?.map(el => {
			if (el?.quantity - negOrderItem[el?.ID] === 0 || el?.quantity < 0) {
				return { ...el, void: true };
			} else {
				return { ...el, void: false };
			}
		})
		?.filter(list => list.void === false);

	const [openShareDialog, setOpenShareDialog] = useState(false);
	const [openSendEmail, setOpenSendEmail] = useState(false);

	const [
		createAttachment,
		{
			data: { createReceiptAttachment } = {
				createReceiptAttachment: {} as any,
			},
			called: createReceiptAttachmentCalled,
			loading: createReceiptAttachmentLoading,
		},
	] = useCreateReceiptAttachmentMutation({});

	const onSubmit = blob => {
		if (!hasAuth || !blob) {
			return;
		}

		createAttachment({
			variables: {
				file: blob,
				billID: billID,
				outletID: outletID,
			},
		});
	};

	useEffect(() => {
		if (!hasAuth || !billCalled || billLoading) {
			return;
		}

		const attachment = document.getElementById('share-receipt') as HTMLElement;
		if (!attachment) {
			return;
		}

		Array.from(attachment.querySelectorAll('img')).forEach(el => {
			const img = el as HTMLImageElement;
			const src = img.currentSrc || img.src || '';
			if (/^https?:\/\//i.test(src) && !src.startsWith(window.location.origin)) {
				img.removeAttribute('src');
				img.style.display = 'none';
			}
		});

		htmlToImage
			.toBlob(attachment, {
				style: { background: '#ffffff' },
				cacheBust: true,
			})
			.then(value => {
				onSubmit(value);
			})
			.catch(() => {});
	}, [hasAuth, billCalled, billLoading, outletLogo]);

	let ttl, subtotal, svc, serviceCharge, tx, tax, total;
	if (orderItems?.length > 0) {
		ttl = orderItems?.map(el => {
			const remainingQty = el?.quantity - (negOrderItem[el?.ID] ?? 0);
			const qtyRatio = el?.quantity > 0 ? remainingQty / el?.quantity : 0;
			return (
				((el?.amount || 0) +
					(el?.taxAmount || 0) +
					(el?.serviceCharge || 0)) *
				qtyRatio
			);
		});

		subtotal = ttl.reduce((accumulator: any, currentValue: any) => {
			return accumulator + currentValue;
		}, 0);

		svc = orderItems?.map(el => {
			return el?.totalInfo?.totalServiceCharge || el?.serviceCharge || 0;
		});

		serviceCharge = svc.reduce((accumulator: any, currentValue: any) => {
			return accumulator + currentValue;
		}, 0);

		tx = orderItems?.map(el => {
			return el?.totalInfo?.totalTax || el?.taxAmount || 0;
		});

		tax = tx.reduce((accumulator: any, currentValue: any) => {
			return accumulator + currentValue;
		}, 0);

		total = subtotal;
	}

	const orderDiscount =
		getBill[0]?.order?.orderDiscount?.filter(
			disc => disc?.status === OrderDiscountStatus.Active,
		)[0]?.amount ?? 0;

	if (orderItems?.length > 0) {
		total = (subtotal || 0) - (orderDiscount || 0);
	}

	const [fs, setFS] = useState(null);
	const [sc, setSC] = useState(null);
	const [ta, setTa] = useState(null);
	const [fd, setFD] = useState(null);
	const [to, setTo] = useState(null);

	useEffect(() => {
		setFS(subtotal);
		setSC(serviceCharge);
		setTa(tax);
		setFD(orderDiscount);
		setTo(total);
	}, [subtotal, serviceCharge, tax, orderDiscount, total]);

	return (
		<>
			{(billLoading || createReceiptAttachmentLoading) && <Loading />}
			<MainHeaderCustomer
				smTitle="Customer App"
				title={outlet?.name || getBill[0]?.outlet?.name}
				mainBtn="none"
				routeSegments={[
					{ name: 'Customer App' },
					{ name: 'Table' },
					{ name: 'Payment', current: true },
					{ name: 'Receipt', current: true },
				]}
			/>
			<ContentWrapper footer float>
				{!billLoading && getBill?.[0] && (
					<>
						{billReceiptDisplay(
							outlet?.name ? outlet : getBill[0]?.outlet,
							outletID,
							getBill,
							orderItems,
							{
								discount: fd,
								subtotal: fs,
								serviceCharge: sc,
								tax: ta,
								total: to,
							},
							negOrderItem,
							false,
							'',
							hotelGuest,
							hotelDebtor,
							officer,
							staff,
							latestPayment,
							(outlet?.currencyCode || getBill[0]?.outlet?.currencyCode) ??
								'',
							outletLogo,
						)}
						{hasAuth &&
							billReceiptDisplay(
								outlet?.name ? outlet : getBill[0]?.outlet,
								outletID,
								getBill,
								orderItems,
								{
									discount: fd,
									subtotal: fs,
									serviceCharge: sc,
									tax: ta,
									total: to,
								},
								negOrderItem,
								true,
								'',
								hotelGuest,
								hotelDebtor,
								officer,
								staff,
								latestPayment,
								(outlet?.currencyCode || getBill[0]?.outlet?.currencyCode) ??
									'',
								outletLogo,
							)}
						<Overlay />
					</>
				)}
				{!billLoading && !getBill?.[0] && (
					<div style={{ padding: 24, textAlign: 'center' }}>
						Receipt not found.
					</div>
				)}
			</ContentWrapper>
			{ShareDialog({
				shareTitle: `Share Receipt - ${getBill[0]?.order?.docNo || ''}`,
				title: `Share Receipt`,
				URL:
					(hasAuth && createReceiptAttachment?.fileURL) ||
					window.location.href,
				setSimple: setOpenShareDialog,
				simple: openShareDialog,
				emailShare: hasAuth ? openSendEmail : false,
				setSimpleEmail: setOpenSendEmail,
			})}
			<Footer
				overlap
				options={[
					{
						name: 'Share',
						onClick: () => {
							setOpenShareDialog(true);
						},
						color: 'primary',
						props: {
							type: 'submit',
						},
						startIcon: <ShareIcon style={{ fontSize: '18px' }} />,
					},
				]}
			/>
		</>
	);
};
